<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { checkId } from '../composables/salesReviewReturnSections';
import SalesReviewReturnSimpleFields from './SalesReviewReturnSimpleFields.vue';

const props = defineProps({
  definition: { type: Object, required: true },
  item: { type: Object, default: null },
  options: { type: Array, default: () => [] },
  readOnly: { type: Boolean, default: false },
  showErrors: { type: Boolean, default: false },
});
const emit = defineEmits(['change']);
const { t } = useI18n();
const expanded = ref(Boolean(props.item));

const enabled = computed(() => Boolean(props.item));
const selectedCheckIds = computed(() =>
  (props.item?.checks || []).map(checkId)
);
const correctionCount = computed(() => selectedCheckIds.value.length);
const incomplete = computed(
  () =>
    enabled.value &&
    (!selectedCheckIds.value.length || !props.item?.observation?.trim())
);

watch(enabled, value => {
  if (value) expanded.value = true;
});

const emitItem = patch => {
  emit('change', { ...props.item, ...patch, section: props.definition.id });
};

const toggleEnabled = event => {
  if (event.target.checked) {
    expanded.value = true;
    emit('change', {
      section: props.definition.id,
      target_key: '',
      checks: [],
      observation: '',
    });
    return;
  }

  emit('change', null);
};

const updateChecks = ids => {
  emitItem({
    checks: props.options.filter(option => ids.includes(checkId(option))),
  });
};
</script>

<template>
  <section
    class="overflow-hidden rounded-lais border bg-white"
    :class="enabled ? 'border-lais-primary' : 'border-lais-border-soft'"
  >
    <div class="flex flex-wrap items-center gap-3 p-4 sm:flex-nowrap">
      <label
        class="flex min-w-0 flex-1 items-center gap-3 text-sm font-semibold text-lais-text"
      >
        <input
          type="checkbox"
          class="size-4 rounded border-lais-border text-lais-primary"
          :checked="enabled"
          :disabled="readOnly"
          @change="toggleEnabled"
        />
        {{ definition.title }}
      </label>
      <span
        v-if="enabled"
        class="rounded-full bg-red-50 px-2 py-1 text-xs font-bold text-red-700"
      >
        {{
          t('SALES.REVIEW.RETURN_FORM.CORRECTION_COUNT', {
            count: correctionCount,
          })
        }}
      </span>
      <p v-else class="min-w-0 flex-1 text-sm text-lais-text-muted">
        {{ definition.description }}
      </p>
      <button
        v-if="enabled"
        type="button"
        class="grid size-8 place-items-center rounded-lais-sm text-lais-text-muted hover:bg-lais-bg-soft"
        :aria-label="t('SALES.REVIEW.RETURN_FORM.TOGGLE')"
        @click="expanded = !expanded"
      >
        <span
          class="size-4"
          :class="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          aria-hidden="true"
        />
      </button>
    </div>

    <div
      v-if="enabled && expanded"
      class="grid gap-4 border-t border-lais-border-soft p-4"
    >
      <SalesReviewReturnSimpleFields
        :definition="definition"
        :incomplete="incomplete"
        :item="item"
        :options="options"
        :read-only="readOnly"
        :show-errors="showErrors"
        @checks="updateChecks"
        @observation="emitItem({ observation: $event })"
      />
    </div>
  </section>
</template>
